import { Component, computed, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, RouterOutlet } from '@angular/router'
import { finalize, forkJoin } from 'rxjs'
import { navItems as sidebarNavItems } from './nav'
import { SidebarFooterComponent } from '../../shared/components/sidebar/sidebar-footer/sidebar-footer.component'
import { SidebarHeaderComponent } from '../../shared/components/sidebar/sidebar-header/sidebar-header.component'
import { SidebarNavComponent } from '../../shared/components/sidebar/sidebar-nav/sidebar-nav.component'
import { SidebarComponent } from '../../shared/components/sidebar/sidebar.component'
import { SecureHeaderComponent } from './secure-header/secure-header.component'
import { SidebarBrandComponent } from '../../shared/components/sidebar/sidebar-brand/sidebar-brand.component'
import { SidebarService } from '../../shared/components/sidebar/sidebar.service'
import { AuthService, GenericError } from '@auth0/auth0-angular'
import { SignalRNotificationService } from '../../core/services/signalr-notification.service'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AppStoreService } from '../../core/app-state.service'
import { NavItem } from '../../core/interfaces/nav/nav-item.interface'
import { TrialBannerComponent } from '../../shared/components/trial-banner/trial-banner.component'
import { StatsService } from '../../core/services/stats.service'
import { NavigationStats } from '../../core/interfaces/me/navigation-stats.interface'
import { PosthogService } from '../../core/services/insights/posthog.service'
import { faBan, faLock } from '@fortawesome/free-solid-svg-icons'
import { ApplicationInsightsService } from '../../core/services/insights/application-insights.service'
import { AsyncPipe } from '@angular/common'
import { FullScreenLoaderComponent } from '../../shared/components/full-screen-loader/full-screen-loader.component'
import { SubscriberAccess } from '../../core/billing/subscriber-access.type'

const recoverableAuthErrorFragments = [
    'login_required',
    'invalid_grant',
    'missing_refresh_token',
    'interaction_required',
    'consent_required',
    'refresh token',
] as const

@Component({
    selector: 'app-secure',
    imports: [
        RouterOutlet,
        SecureHeaderComponent,
        SidebarComponent,
        SidebarHeaderComponent,
        SidebarBrandComponent,
        SidebarNavComponent,
        SidebarFooterComponent,
        TrialBannerComponent,
        FullScreenLoaderComponent,
        AsyncPipe,
    ],
    templateUrl: './secure-layout.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './secure-layout.component.scss',
})
export class SecureLayoutComponent implements OnInit {
    #applicationInsightsService = inject(ApplicationInsightsService)
    appStore = inject(AppStoreService)
    #authService = inject(AuthService)
    #destroyRef = inject(DestroyRef)
    #signalrService = inject(SignalRNotificationService)
    #router = inject(Router)
    #sidebarService = inject(SidebarService)
    #statsService = inject(StatsService)
    #posthogService = inject(PosthogService)
    #isRedirectingToLogin = false

    isLoadingAuth = toSignal(this.#authService.isLoading$, { initialValue: true })
    isLoadingInitialData = signal(true)

    baseNavItems: NavItem[] = sidebarNavItems

    navigationStats = signal<NavigationStats | null>(null)

    /** Filtered nav items based on user role and stats */
    filteredNavItems = computed(() => {
        const filteredNavItems = this.#filterNavItems(this.baseNavItems)
        const navigationStats = this.navigationStats()

        if (navigationStats) {
            return filteredNavItems.map((item) => {
                if (item.statProperty && navigationStats) {
                    const count = navigationStats[item.statProperty as keyof NavigationStats] ?? 0
                    return { ...item, badge: { color: item.badge?.color ?? 'primary', text: count.toString() } }
                }
                return item
            })
        }

        return filteredNavItems
    })

    /**
     * Recursively filters nav items based on user roles and permissions.
     * Support users see everything, Admin users see admin and regular items,
     * regular users only see items without role requirements.
     */
    #filterNavItems(items: NavItem[]): NavItem[] {
        const isSupport = this.appStore.isSupport()
        const isAdmin = this.appStore.isAdmin()
        const isFullSubscriber = this.appStore.me()?.subscriberAccessType === SubscriberAccess.Full

        return items
            .map((item) => {
                const authorized = this.#isNavItemAuthorized(item, isSupport, isAdmin, isFullSubscriber)

                if (!authorized && item.unauthorizedBehavior !== 'disabled') {
                    return null
                }

                const children = item.children ? this.#filterNavItems(item.children) : undefined

                return {
                    ...item,
                    children,
                    attributes: { ...item.attributes, disabled: !authorized },
                    faIcon: !authorized ? faLock : item.faIcon,
                }
            })
            .filter((item) => item !== null)
    }

    #isNavItemAuthorized(item: NavItem, isSupport: boolean, isAdmin: boolean, isFullSubscriber: boolean): boolean {
        if (item.requiresSupport && !isSupport) {
            return false
        }

        if (item.requiresAdmin && !isAdmin) {
            return false
        }

        if (item.requiresSubscription && !isFullSubscriber) {
            return false
        }

        if (item.permissions) {
            const permissions = Array.isArray(item.permissions) ? item.permissions : [item.permissions]

            if (!this.appStore.hasAnyPermission(permissions)) {
                return false
            }
        }

        return true
    }

    ngOnInit(): void {
        this.#authService.error$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((error) => {
            if (this.#redirectToLoginForAuthError(error)) return

            console.error('Auth error:', error)
        })

        forkJoin({
            me: this.appStore.getMe(),
            company: this.appStore.getCompany(),
        })
            .pipe(finalize(() => this.isLoadingInitialData.set(false)))
            .subscribe({
                next: ({ me, company }) => {
                    this.#applicationInsightsService.identifyUser(me.id, company.companyId)
                    this.#signalrService.startConnection().catch((error) => {
                        if (!this.#redirectToLoginForAuthError(error)) {
                            console.error('SignalR connection failed:', error)
                        }
                    })
                    this.getNavigationStats()
                },
                error: (error) => {
                    if (this.#redirectToLoginForAuthError(error)) {
                        return
                    }

                    console.error('Error fetching initial data:', error)
                    this.#router.navigate(['/error'])
                },
            })

        const sub = this.#signalrService.menuStatCounterReceived$.subscribe({
            next: (notification) => {
                this.navigationStats.update((stats) => {
                    if (stats) {
                        return { ...stats, [notification.name]: notification.value }
                    }
                    return stats
                })
            },
        })

        this.#destroyRef.onDestroy(() => {
            this.#signalrService.stopConnection()
            sub.unsubscribe()
        })
    }

    getNavigationStats(): void {
        this.#statsService
            .getNavigationStats()
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: (stats) => this.navigationStats.set(stats),
                error: (error) => {
                    if (!this.#redirectToLoginForAuthError(error)) {
                        console.error('Error fetching navigation stats:', error)
                    }
                },
            })
    }

    #redirectToLoginForAuthError(error: unknown): boolean {
        if (!this.#isRecoverableAuthError(error)) {
            return false
        }

        if (this.#isRedirectingToLogin) {
            return true
        }

        this.#isRedirectingToLogin = true
        this.appStore.clear()
        void this.#signalrService.stopConnection()
        this.#authService.loginWithRedirect({ appState: { target: this.#router.url } }).subscribe()
        return true
    }

    #isRecoverableAuthError(error: unknown): boolean {
        if (error instanceof GenericError && this.#containsRecoverableAuthError(error.error)) {
            return true
        }

        if (typeof error === 'string') {
            return this.#containsRecoverableAuthError(error)
        }

        if (!error || typeof error !== 'object') {
            return false
        }

        const authError = error as {
            error?: unknown
            error_description?: unknown
            message?: unknown
            name?: unknown
        }

        return [authError.error, authError.error_description, authError.message, authError.name].some((value) =>
            this.#containsRecoverableAuthError(value)
        )
    }

    #containsRecoverableAuthError(value: unknown): boolean {
        if (typeof value !== 'string') {
            return false
        }

        const normalizedValue = value.toLowerCase()
        return recoverableAuthErrorFragments.some((fragment) => normalizedValue.includes(fragment))
    }

    toggleMenu(): void {
        this.#sidebarService.toggle({ toggle: 'visible' })
    }

    logout(): void {
        this.#authService.logout()
        this.#posthogService.logoutUser()
    }
}
